/**
 * *Positions Groups Excluded*: what the control means, kept out of the
 * component.
 *
 * A statistic may declare the Canonical Position Groups it does not rank. The
 * screens render that as a dropdown of the sport's groups with one or more
 * selected, and three questions about it are not React's:
 *
 * - **what an empty selection is called.** "No exclusions" is a value and not a
 *   blank: a cell that printed nothing would read as a column nobody has filled
 *   in yet, on the one screen every source in the sport reads.
 * - **what order the selection is stored in.** The server stores the sport's own
 *   order, so a draft that kept click order would compare unequal to a row it
 *   matches and send a patch that changes nothing. `orderedBy` is that rule, and
 *   `sameGroups` is the comparison the diff needs.
 * - **which groups a row may offer.** The dictionary holds six sports in one
 *   table and the groups are the row's *own* sport's — football has no `GK` and
 *   the server refuses one. A stored group the sport no longer offers is still
 *   shown, for the reason `statOptions` shows an out-of-sport statistic: a
 *   control that silently dropped a stored value would report a declaration the
 *   entry still holds as one it does not.
 *
 * No imports, so `npm test` can execute it directly: the frontend ships no test
 * runner and bare `node --experimental-strip-types` cannot load React or Vite.
 */

/** What an empty selection is called, on every screen that shows one. */
export const NO_EXCLUSIONS = "No exclusions";

/**
 * The groups a sport offers, as the server publishes them.
 *
 * Keyed by sport because the dictionary table shows every sport at once and a
 * row's options are its own sport's.
 *
 * **Every sport the endpoint answers for has at least one group.** A sport with
 * no position vocabulary offers `UNKNOWN` alone — which is honest rather than
 * empty, because `UNKNOWN` is the cohort every one of its players is actually
 * in (ADR-0026 prefers that to borrowing another sport's map). So the empty
 * list below means the query has not resolved, never "this sport has none".
 */
export type PositionGroups = Record<string, PositionGroupOffer[]>;

/**
 * One group the sport offers, and the publisher labels that feed it.
 *
 * **The labels travel with the group because a group is made of them.** A group
 * is the value `canonicalise` reads out of a map, so one no label feeds has no
 * members — and a group somebody has just invented is indistinguishable from a
 * typo until its labels are shown beside it. That is the question the control
 * could not answer: having added `abc`, there was nothing on the screen saying
 * what `abc` was.
 *
 * Empty for `UNKNOWN`, which nothing maps to — that is what makes it the
 * fallback rather than an absence of data.
 */
export interface PositionGroupOffer {
  group: string;
  labels: string[];
}

/**
 * The groups a row may show, which is its sport's plus anything it holds.
 *
 * `undefined` while the query is in flight, which is the only way the result is
 * empty: see `PositionGroups` above. The control says so rather than drawing an
 * empty list.
 */
export function optionsFor(
  offered: PositionGroups | undefined,
  sport: string,
  selected: readonly string[],
): string[] {
  const groups = (offered?.[sport] ?? []).map((offer) => offer.group);
  // A stored group the sport does not offer is appended rather than dropped.
  // It is reachable: a Source Profile's `position_map` can name a group of its
  // own, and deleting that profile stops the sport offering it while every
  // entry excluding it still does. Hiding it would make the row's own
  // declaration invisible from the control that edits it.
  const extra = selected.filter((group) => !groups.includes(group));
  return [...groups, ...extra];
}

/**
 * What feeds each of a sport's groups, for the control that lists them.
 *
 * Keyed by group rather than returned as a list so a renderer can look one up
 * beside the name it is already drawing, and so a group the sport no longer
 * offers — which `optionsFor` still shows — simply has no entry rather than
 * needing a parallel lookup that could disagree.
 */
export function labelsFor(
  offered: PositionGroups | undefined,
  sport: string,
): Record<string, string[]> {
  const found: Record<string, string[]> = {};
  for (const offer of offered?.[sport] ?? []) found[offer.group] = offer.labels;
  return found;
}

/**
 * A selection in the sport's own order, deduplicated.
 *
 * The order the server stores, so a draft can be compared against a saved row
 * without normalising at the comparison. Anything the sport does not offer
 * keeps its relative order at the end, where `optionsFor` puts it.
 */
export function orderedBy(options: readonly string[], groups: readonly string[]): string[] {
  const wanted = new Set(groups);
  const known = options.filter((group) => wanted.has(group));
  const unknown = groups.filter((group) => !options.includes(group));
  return [...known, ...dedupe(unknown)];
}

/** Add a group to the selection, or take it out — in the sport's order. */
export function toggle(
  options: readonly string[],
  groups: readonly string[],
  group: string,
): string[] {
  const next = groups.includes(group)
    ? groups.filter((held) => held !== group)
    : [...groups, group];
  return orderedBy(options, next);
}

/**
 * Whether two selections say the same thing.
 *
 * Order-insensitive, which is the whole reason it exists rather than a
 * `join()` comparison: the stored list is in the sport's order and a draft is
 * in whatever order the boxes were ticked, so comparing the two literally
 * would report every opened row as edited and send a patch on Save that writes
 * back what is already there.
 */
export function sameGroups(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const held = new Set(a);
  return b.every((group) => held.has(group));
}

/**
 * The selection as one line of text.
 *
 * `limit` caps how many are named before the rest become a count, so a
 * statistic excluding nine groups does not widen the table column to fit them.
 * Zero is "No exclusions" — the client's own wording — and never a blank.
 */
export function summarise(groups: readonly string[], limit = 3): string {
  if (groups.length === 0) return NO_EXCLUSIONS;
  if (groups.length <= limit) return groups.join(", ");
  return `${groups.slice(0, limit).join(", ")} +${groups.length - limit} more`;
}

/**
 * What the control says it will do, for the hint under it.
 *
 * Named rather than counted, up to the same cap, because the sentence is read
 * while the decision is being made: "GK is not ranked on this" is the check
 * somebody wants, and "3 position groups excluded" is not.
 *
 * **Canonical Position Group, in full, in the prose.** `CONTEXT.md` keeps bare
 * "position group" and "position" off the vocabulary for this, because the
 * thing excluded is the normalised group a cohort is built from and not the
 * label a publisher sent. The *control's caption* is the client's "Positions
 * Groups Excluded", which is a name rather than prose and stays as they asked.
 */
export function exclusionHint(groups: readonly string[]): string | null {
  if (groups.length === 0) {
    // **Nothing, rather than "every group is ranked".** The shut control
    // already says "No exclusions" two lines above, so a sentence restating it
    // is a second line of grey text under a control that has not been touched —
    // which is what makes a form read as documentation. This line exists to
    // describe a *selection*, so with no selection it has nothing to say.
    return null;
  }
  const named = summarise(groups, 4);
  const verb = groups.length === 1 ? "is" : "are";
  return `${named} ${verb} not ranked on this statistic, and ${
    groups.length === 1 ? "its" : "their"
  } values are kept out of the comparison.`;
}

function dedupe(groups: readonly string[]): string[] {
  return [...new Set(groups)];
}
